// Tabella "Diete / Intolleranze" per Tabelle Fisse Alunni.
// Replica la tabella diete di Gestione Mensa condividendo GLI STESSI dati
// (collection mensa_intolleranze + elenco tipi in app_settings.mensa_diete).
import { useEffect, useMemo, useState, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import AnnoAttivoSelector from "@/components/AnnoAttivoSelector";
import DieteManagerModal from "@/components/DieteManagerModal";

// Le intolleranze/diete multiple sono memorizzate come stringa separata da virgola
// (retrocompatibile con Mensa e Scheda Alunno che leggono lo stesso campo `dieta`).
const splitDiete = (s) => String(s || "").split(",").map((x) => x.trim()).filter(Boolean);

function DietaMultiSelect({ testId, options, value, disabled, saving, prevYear, onChange, alunnoLabel }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  const sel = splitDiete(value);
  const allOpts = Array.from(new Set([...(options || []), ...sel]));
  const summary = sel.length ? sel.join(", ") : "— nessuna —";
  const toggle = (opt) => {
    const next = sel.includes(opt) ? sel.filter((x) => x !== opt) : [...sel, opt];
    onChange(next.join(", "));
  };
  return (
    <>
      <button type="button" data-testid={testId} disabled={disabled} onClick={() => setOpen(true)}
        title={disabled ? "C.F. mancante, impossibile modificare" : "Apri l'elenco intolleranze/diete (selezione multipla)"}
        style={{ width: "100%", textAlign: "left", padding: "4px 6px", border: "1px solid #ce93d8", borderRadius: 3, fontSize: 12,
                 fontWeight: sel.length ? 700 : 400, color: sel.length ? "#ad1457" : "#999", textTransform: "uppercase",
                 background: prevYear ? "#fff8e1" : "white", cursor: disabled ? "not-allowed" : "pointer",
                 display: "flex", justifyContent: "space-between", gap: 6, alignItems: "center" }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{saving ? "Salvataggio…" : summary}</span>
        <span style={{ fontSize: 10, opacity: 0.6 }}>▼</span>
      </button>
      {open && (
        <div onMouseDown={() => setOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div data-testid={`${testId}-panel`} onMouseDown={(e) => e.stopPropagation()}
            style={{ background: "white", borderRadius: 8, width: 460, maxWidth: "94vw", maxHeight: "82vh", display: "flex", flexDirection: "column", boxShadow: "0 12px 48px rgba(0,0,0,0.35)", overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", background: "#6a1b9a", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>🍴 Intolleranze / Diete{alunnoLabel ? ` — ${alunnoLabel}` : ""}</div>
              <button type="button" onClick={() => setOpen(false)} data-testid={`${testId}-close`}
                style={{ background: "transparent", border: "none", color: "white", fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: "6px 8px", background: "#f3e5f5", fontSize: 11, color: "#6a1b9a", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>{sel.length ? `${sel.length} selezionate` : "Nessuna selezione"} · salvataggio immediato</span>
              {sel.length > 0 && (
                <button type="button" onClick={() => onChange("")} data-testid={`${testId}-clear`}
                  style={{ padding: "3px 8px", fontSize: 11, color: "#c62828", background: "white", border: "1px solid #ef9a9a", borderRadius: 3, cursor: "pointer", fontWeight: 700 }}>✕ Svuota tutte</button>
              )}
            </div>
            <div style={{ overflowY: "auto", padding: 8, flex: 1 }}>
              {allOpts.map((o) => {
                const checked = sel.includes(o);
                return (
                  <label key={o} data-testid={`${testId}-opt-${o}`}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 8px", cursor: "pointer", fontSize: 13, borderRadius: 4, background: checked ? "#f3e5f5" : "transparent", textTransform: "uppercase", borderBottom: "1px solid #f0f0f0" }}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(o)} style={{ width: 17, height: 17 }} />
                    <span style={{ fontWeight: checked ? 700 : 400, color: checked ? "#ad1457" : "#333" }}>{o}</span>
                  </label>
                );
              })}
              {allOpts.length === 0 && <div style={{ padding: 12, fontSize: 12, color: "#999" }}>Nessun tipo dieta configurato. Usa &quot;Gestisci tipi dieta&quot;.</div>}
            </div>
            <div style={{ padding: "10px 16px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end" }}>
              <button type="button" onClick={() => setOpen(false)} data-testid={`${testId}-done`}
                style={{ padding: "8px 20px", background: "#6a1b9a", color: "white", border: "none", borderRadius: 4, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Chiudi</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function DieteAlunniPage() {
  const { annoCorrente } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");
  const [filterDieta, setFilterDieta] = useState("");
  const [filterSettore, setFilterSettore] = useState("");
  const [soloConDieta, setSoloConDieta] = useState(true);
  const [dieteList, setDieteList] = useState([]);
  const [dieteModalOpen, setDieteModalOpen] = useState(false);
  const [savingCf, setSavingCf] = useState(null);
  const [docMap, setDocMap] = useState({});
  const [docBusyCf, setDocBusyCf] = useState(null);
  const [sendModal, setSendModal] = useState(null); // { cf, nominativo, email, emails, sending }
  const fileRef = useRef(null);
  const uploadTargetRef = useRef(null); // { cf, nominativo }

  const loadDocs = async () => {
    try { const r = await api.get("/diete-doc"); setDocMap(r.data?.items || {}); }
    catch { /* noop */ }
  };

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const params = {};
      if (annoCorrente) params.anno = annoCorrente;
      const r = await api.get("/mensa-matrice/matrice", { params });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };
  const loadDiete = async () => {
    try { const r = await api.get("/mensa/giorno/diete"); setDieteList(r.data?.items || []); }
    catch { /* noop */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [annoCorrente]);
  useEffect(() => { loadDiete(); loadDocs(); }, []);

  const setDieta = async (row, dieta, note) => {
    const noteToSend = note !== undefined ? note : (row.dieta_note || "");
    setSavingCf(row.cf);
    try {
      await api.put("/mensa-matrice/intolleranze/by-cf", {
        cf: row.cf,
        dieta: dieta || "",
        note: noteToSend,
        studente: `${row.cognome} ${row.nome}`.trim(),
        anno: annoCorrente || undefined,
      });
      setData((d) => ({ ...d, rows: (d?.rows || []).map((r) => r.cf === row.cf ? { ...r, dieta, dieta_note: noteToSend, dieta_prev_year: false } : r) }));
    } catch (e) {
      alert("Errore salvataggio: " + (e.response?.data?.detail || e.message));
    } finally { setSavingCf(null); }
  };
  const numeraEsistenti = async () => {
    if (!window.confirm("Assegnare il n° di registrazione progressivo alle diete esistenti che non ce l'hanno ancora?")) return;
    try {
      const r = await api.post("/mensa-matrice/intolleranze/numera");
      alert(`Registrazioni assegnate: ${r.data?.assegnati ?? 0} (ultimo n° ${r.data?.ultimo ?? "-"} per l'anno ${r.data?.anno ?? "-"}).`);
      load();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  // --- Documento dieta (.docx → PDF, stampa, invio email) --------------------
  const triggerUpload = (row) => {
    if (!row.cf) { alert("C.F. mancante: impossibile allegare il documento."); return; }
    uploadTargetRef.current = { cf: row.cf, nominativo: `${row.cognome || ""} ${row.nome || ""}`.trim() };
    if (fileRef.current) { fileRef.current.value = ""; fileRef.current.click(); }
  };
  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    const tgt = uploadTargetRef.current;
    if (!file || !tgt) return;
    if (!file.name.toLowerCase().endsWith(".docx")) { alert("Seleziona un file in formato .docx"); return; }
    setDocBusyCf(tgt.cf);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("nominativo", tgt.nominativo || "");
      const r = await api.post(`/diete-doc/${encodeURIComponent(tgt.cf)}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocMap((m) => ({ ...m, [tgt.cf]: {
        filename: r.data?.filename, nominativo: r.data?.nominativo, size: r.data?.size,
        updated_at: r.data?.updated_at, uploaded_at: r.data?.updated_at, last_sent_at: null, last_sent_to: null,
      } }));
    } catch (err) {
      alert("Errore caricamento: " + (err.response?.data?.detail || err.message));
    } finally { setDocBusyCf(null); uploadTargetRef.current = null; }
  };
  const printDoc = async (cf) => {
    setDocBusyCf(cf);
    try {
      const r = await api.get(`/diete-doc/${encodeURIComponent(cf)}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank");
      if (!w) alert("Popup bloccato dal browser. Abilita i popup per visualizzare/stampare il PDF.");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      alert("Errore apertura PDF: " + (err.response?.data?.detail || err.message));
    } finally { setDocBusyCf(null); }
  };
  const openSend = async (row) => {
    const cf = row.cf;
    const nominativo = docMap[cf]?.nominativo || `${row.cognome || ""} ${row.nome || ""}`.trim();
    setSendModal({ cf, nominativo, email: "", emails: [], sending: false, loading: true, userEdited: false });
    try {
      const r = await api.get(`/diete-doc/${encodeURIComponent(cf)}/email-famiglia`);
      setSendModal((s) => {
        if (!s || s.cf !== cf) return s;
        // Non sovrascrivere se l'utente ha già digitato qualcosa nel frattempo.
        const email = s.userEdited ? s.email : (r.data?.default || "");
        return { ...s, email, emails: r.data?.emails || [], loading: false };
      });
    } catch {
      setSendModal((s) => s && s.cf === cf ? { ...s, loading: false } : s);
    }
  };
  const doSend = async () => {
    const sm = sendModal;
    if (!sm || sm.loading) return;
    const email = (sm.email || "").trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { alert("Inserisci un indirizzo email valido."); return; }
    if (!window.confirm(`Inviare la dieta di ${sm.nominativo || "questo alunno"} all'indirizzo ${email}?`)) return;
    setSendModal((s) => ({ ...s, sending: true }));
    try {
      const r = await api.post(`/diete-doc/${encodeURIComponent(sm.cf)}/invia`, { email });
      setDocMap((m) => ({ ...m, [sm.cf]: { ...(m[sm.cf] || {}), last_sent_at: r.data?.sent_at, last_sent_to: r.data?.to } }));
      setSendModal(null);
      alert(`Email inviata a ${email}.`);
    } catch (err) {
      alert("Errore invio: " + (err.response?.data?.detail || err.message));
      setSendModal((s) => s ? { ...s, sending: false } : s);
    }
  };
  const deleteDoc = async (cf) => {
    if (!window.confirm("Eliminare il documento dieta allegato a questo alunno?")) return;
    setDocBusyCf(cf);
    try {
      await api.delete(`/diete-doc/${encodeURIComponent(cf)}`);
      setDocMap((m) => { const n = { ...m }; delete n[cf]; return n; });
    } catch (err) {
      alert("Errore eliminazione: " + (err.response?.data?.detail || err.message));
    } finally { setDocBusyCf(null); }
  };

  const rows = useMemo(() => {
    const all = (data?.rows || []);
    const s = search.trim().toLowerCase();
    return all.filter((r) => {
      if (soloConDieta && !(r.dieta || "").trim()) return false;
      if (filterDieta && !splitDiete(r.dieta).includes(filterDieta)) return false;
      if (filterSettore && r.settore !== filterSettore) return false;
      if (s && !`${r.cognome} ${r.nome} ${r.cf}`.toLowerCase().includes(s)) return false;
      return true;
    }).sort((a, b) =>
      (a.cognome || "").localeCompare(b.cognome || "", "it") ||
      (a.nome || "").localeCompare(b.nome || "", "it"));
  }, [data, search, filterDieta, filterSettore, soloConDieta]);

  const dieteOpts = useMemo(() => Array.from(new Set((data?.rows || []).flatMap((r) => splitDiete(r.dieta)))).sort(), [data]);
  const settoriOpts = useMemo(() => Array.from(new Set((data?.rows || []).map((r) => r.settore).filter(Boolean))).sort(), [data]);
  const perDieta = useMemo(() => {
    const m = {};
    for (const r of rows) { for (const d of splitDiete(r.dieta)) m[d] = (m[d] || 0) + 1; }
    return m;
  }, [rows]);

  const stampaElenco = () => {
    if (rows.length === 0) { alert("Nessun alunno da stampare."); return; }
    const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const annoLabel = annoCorrente ? `${annoCorrente}/${(annoCorrente + 1) % 100}` : "—";
    const oggi = new Date().toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
    const filtri = [
      filterSettore ? `Settore: ${filterSettore}` : null,
      filterDieta ? `Dieta: ${filterDieta.toUpperCase()}` : null,
      search.trim() ? `Ricerca: "${search.trim()}"` : null,
      soloConDieta ? "Solo con dieta" : "Tutti gli alunni",
    ].filter(Boolean).join(" · ");
    const riepilogo = Object.entries(perDieta).sort().map(([k, v]) => `${esc(k.toUpperCase())}: ${v}`).join(" · ") || "—";
    const trs = rows.map((r, i) => `
      <tr>
        <td class="num">${i + 1}</td>
        <td class="strong">${esc(r.settore || "—")}</td>
        <td class="mono">${esc(r.classe || "—")}</td>
        <td class="strong">${esc(`${r.cognome || ""} ${r.nome || ""}`.trim().toUpperCase())}</td>
        <td class="mono">${esc(r.cf || "—")}</td>
        <td class="mono" style="text-align:center;font-weight:700;color:#6a1b9a">${esc(r.dieta_reg || "—")}</td>
        <td class="dieta">${esc((r.dieta || "—").toUpperCase())}${r.dieta_prev_year ? ' <span class="warn">(a.s. prec.)</span>' : ""}</td>
        <td>${r.dieta_note ? esc(r.dieta_note) : "—"}</td>
      </tr>`).join("");
    const html = `<!doctype html>
<html lang="it"><head><meta charset="utf-8"><title>Diete / Intolleranze · ${esc(annoLabel)}</title>
<style>
  @page { size: A4; margin: 12mm 14mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; color: #222; }
  .hdr { border-bottom: 2px solid #8e24aa; padding-bottom: 6px; margin-bottom: 8px; }
  .title { font-size: 18px; font-weight: 800; color: #6a1b9a; }
  .meta { margin-top: 4px; font-size: 12px; color: #0d47a1; font-weight: 600; }
  .meta .sep { margin: 0 6px; color: #999; }
  .filters { margin-top: 3px; font-size: 11px; color: #555; }
  .tbl { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 6px; }
  .tbl thead tr { background: #8e24aa; color: #fff; }
  .tbl th, .tbl td { border: 1px solid #cfd8dc; padding: 4px 6px; text-align: left; }
  .tbl thead { display: table-header-group; }
  .tbl tr { page-break-inside: avoid; }
  .tbl td.strong { font-weight: 700; }
  .tbl td.mono { font-family: Consolas, "Courier New", monospace; color: #555; white-space: nowrap; }
  .tbl td.num { text-align: right; width: 28px; color: #666; }
  .tbl td.dieta { background: #fce4ec; font-weight: 700; color: #880e4f; }
  .warn { color: #e65100; font-weight: 600; }
  .ft { margin-top: 10px; font-size: 9px; color: #888; border-top: 1px dashed #ccc; padding-top: 4px; text-align: right; }
</style>
</head><body>
  <div class="hdr">
    <div class="title">🍴 Diete / Intolleranze alunni</div>
    <div class="meta">A.S. ${esc(annoLabel)} <span class="sep">·</span> Totale: ${rows.length} alunni <span class="sep">·</span> ${riepilogo}</div>
    <div class="filters">${esc(filtri)}</div>
  </div>
  <table class="tbl">
    <thead><tr><th>#</th><th>Sett.</th><th>Classe</th><th>Alunno</th><th>C.F.</th><th>N° reg.</th><th>Dieta</th><th>Note</th></tr></thead>
    <tbody>${trs}</tbody>
  </table>
  <footer class="ft">canossaHUB · Diete/Intolleranze · A.S. ${esc(annoLabel)} · stampato il ${esc(oggi)}</footer>
  <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 250); };<\/script>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) { alert("Popup bloccato dal browser. Abilita i popup per stampare."); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  return (
    <div data-testid="diete-alunni-page" style={{ padding: "12px 20px" }}>
      <div className="sw-page-title">
        <h1>🍴 Diete / Intolleranze</h1>
        <div className="sw-breadcrumb">Tabelle Fisse &gt; Diete</div>
        <button onClick={stampaElenco} data-testid="diete-alunni-print-btn"
          title="Stampa l'elenco filtrato delle diete/intolleranze"
          style={{ marginLeft: "auto", padding: "7px 14px", background: "#6a1b9a", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
          🖨️ Stampa elenco
        </button>
        <button onClick={numeraEsistenti} data-testid="diete-alunni-numera-btn"
          title="Assegna il n° di registrazione progressivo alle diete esistenti che non ce l'hanno ancora"
          style={{ marginLeft: 8, padding: "7px 14px", background: "#00695c", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
          🔢 Numera esistenti
        </button>
        <button onClick={() => setDieteModalOpen(true)} data-testid="diete-alunni-manage-btn"
          title="Gestisci l'elenco dei tipi di dieta (condiviso con Gestione Mensa)"
          style={{ marginLeft: 8, padding: "7px 14px", background: "#1a237e", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
          ⚙ Gestisci tipi dieta
        </button>
      </div>

      <div style={{ margin: "8px 0", background: "white", border: "1px solid #ccc", padding: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <AnnoAttivoSelector />
        <input placeholder="Cerca alunno o C.F.…" value={search} onChange={(e) => setSearch(e.target.value)}
          data-testid="diete-alunni-search"
          style={{ padding: "5px 10px", border: "1px solid #ccc", fontSize: 12, width: 220, marginLeft: 8 }} />
        <select value={filterSettore} onChange={(e) => setFilterSettore(e.target.value)} data-testid="diete-alunni-filter-settore"
          style={{ padding: "5px 8px", border: "1px solid #ccc", fontSize: 12 }}>
          <option value="">Tutti i settori</option>
          {settoriOpts.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filterDieta} onChange={(e) => setFilterDieta(e.target.value)} data-testid="diete-alunni-filter-dieta"
          style={{ padding: "5px 8px", border: "1px solid #ccc", fontSize: 12, textTransform: "uppercase" }}>
          <option value="">Tutte le diete</option>
          {dieteOpts.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <label style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer" }}
          title="Se disattivo, mostra tutti gli alunni (anche senza dieta) così puoi assegnarne una">
          <input type="checkbox" checked={soloConDieta} onChange={(e) => setSoloConDieta(e.target.checked)} data-testid="diete-alunni-solo" />
          Solo con dati inseriti
        </label>
        <div style={{ marginLeft: "auto", fontSize: 12, color: "#444" }}>
          <b>{rows.length}</b> alunni · {Object.entries(perDieta).map(([k, v]) => `${k.toUpperCase()}: ${v}`).join(" · ") || "—"}
        </div>
      </div>

      {err && <div style={{ margin: "0 0 8px", padding: 10, background: "#fff0f0", border: "1px solid #fcc", color: "#c00", fontSize: 12 }}>{err}</div>}

      <div style={{ background: "white", border: "1px solid #ccc", overflow: "auto", maxHeight: "72vh" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }} data-testid="diete-alunni-table">
          <thead style={{ background: "#8e24aa", color: "white", position: "sticky", top: 0 }}>
            <tr>
              <th style={{ padding: "6px 8px", textAlign: "center", width: 30 }}>#</th>
              <th style={{ padding: "6px 8px", textAlign: "left", width: 60 }}>SETT.</th>
              <th style={{ padding: "6px 8px", textAlign: "left", width: 80 }}>CLASSE</th>
              <th style={{ padding: "6px 8px", textAlign: "left" }}>ALUNNO</th>
              <th style={{ padding: "6px 8px", textAlign: "left", width: 140 }}>C.F.</th>
              <th style={{ padding: "6px 8px", textAlign: "center", width: 80 }} title="Numero di registrazione progressivo (anno/numero)">N° REG.</th>
              <th style={{ padding: "6px 8px", textAlign: "left", width: 200, background: "#ad1457" }}>🍴 DIETA</th>
              <th style={{ padding: "6px 8px", textAlign: "left", width: 240, background: "#ad1457" }}>✍ NOTE</th>
              <th style={{ padding: "6px 8px", textAlign: "left", width: 230, background: "#4a148c" }} title="Documento dieta (.docx) → PDF, stampa e invio email">📄 DOCUMENTO DIETA</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} style={{ padding: 20, textAlign: "center", color: "#888" }}>Caricamento…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={9} style={{ padding: 30, textAlign: "center", color: "#888" }}>Nessun alunno.</td></tr>}
            {!loading && rows.map((r, i) => (
              <tr key={r.cf || i} data-testid={`diete-alunni-row-${i}`} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ padding: "5px 8px", textAlign: "center", color: "#888", fontFamily: "monospace" }}>{i + 1}</td>
                <td style={{ padding: "5px 8px", fontWeight: 700 }}>{r.settore || "—"}</td>
                <td style={{ padding: "5px 8px", fontFamily: "monospace" }}>{r.classe || "—"}</td>
                <td style={{ padding: "5px 8px", fontWeight: 600, textTransform: "uppercase" }}>{r.cognome} {r.nome}</td>
                <td style={{ padding: "5px 8px", fontFamily: "monospace", fontSize: 10, color: "#666" }}>{r.cf || "—"}</td>
                <td style={{ padding: "5px 8px", textAlign: "center", fontFamily: "monospace", fontWeight: 700, color: r.dieta_reg ? "#6a1b9a" : "#bbb" }} data-testid={`diete-alunni-reg-${i}`}>{r.dieta_reg || "—"}</td>
                <td style={{ padding: "5px 8px", verticalAlign: "top" }}>
                  <DietaMultiSelect
                    testId={`diete-alunni-select-${i}`}
                    options={dieteList}
                    value={r.dieta || ""}
                    disabled={!r.cf || savingCf === r.cf}
                    saving={savingCf === r.cf}
                    prevYear={r.dieta_prev_year}
                    alunnoLabel={`${r.cognome || ""} ${r.nome || ""}`.trim()}
                    onChange={(v) => setDieta(r, v)}
                  />
                  {r.dieta_prev_year && <div style={{ fontSize: 9, color: "#e65100", marginTop: 2 }}>⚠ dieta anno precedente</div>}
                </td>
                <td style={{ padding: "5px 8px", verticalAlign: "top" }}>
                  <textarea
                    key={`note-${r.cf}-${r.dieta_note || ""}`}
                    defaultValue={r.dieta_note || ""}
                    rows={3}
                    placeholder="✍ Nota / descrizione dieta…"
                    data-testid={`diete-alunni-note-${i}`}
                    disabled={!r.cf || savingCf === r.cf}
                    onBlur={(e) => { if ((e.target.value || "") !== (r.dieta_note || "")) setDieta(r, r.dieta, e.target.value); }}
                    title="Nota libera / descrizione dettagliata della dieta. Per la dieta 'Altro' compare anche nella pagina dettagli del report refettori."
                    style={{ width: "100%", fontSize: 11, padding: "3px 6px", border: "1px solid #ce93d8", borderRadius: 3, background: "#fff8e1", resize: "vertical", fontFamily: "inherit" }}
                  />
                </td>
                <td style={{ padding: "5px 8px", verticalAlign: "top" }}>
                  {(() => {
                    const doc = r.cf ? docMap[r.cf] : null;
                    const busy = docBusyCf === r.cf;
                    if (!doc) {
                      return (
                        <button type="button" data-testid={`diete-doc-upload-${i}`} disabled={!r.cf || busy}
                          onClick={() => triggerUpload(r)}
                          title={r.cf ? "Carica il documento dieta in formato .docx (verrà convertito in PDF)" : "C.F. mancante"}
                          style={{ padding: "5px 10px", fontSize: 11, fontWeight: 700, color: "white", background: r.cf ? "#4a148c" : "#bbb",
                                   border: "none", borderRadius: 3, cursor: r.cf && !busy ? "pointer" : "not-allowed", whiteSpace: "nowrap" }}>
                          {busy ? "⏳ Conversione…" : "📎 Carica .docx"}
                        </button>
                      );
                    }
                    const sentLabel = doc.last_sent_at ? new Date(doc.last_sent_at).toLocaleDateString("it-IT") : null;
                    return (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <div title={doc.filename} style={{ fontSize: 10, color: "#4a148c", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 210 }}>
                          📄 {doc.filename || "documento.docx"}
                        </div>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          <button type="button" data-testid={`diete-doc-print-${i}`} disabled={busy} onClick={() => printDoc(r.cf)}
                            title="Apri il PDF per visualizzarlo / stamparlo"
                            style={{ padding: "4px 8px", fontSize: 11, fontWeight: 700, color: "white", background: "#6a1b9a", border: "none", borderRadius: 3, cursor: busy ? "wait" : "pointer" }}>🖨️ Stampa</button>
                          <button type="button" data-testid={`diete-doc-send-${i}`} disabled={busy} onClick={() => openSend(r)}
                            title="Invia il PDF via email alla famiglia"
                            style={{ padding: "4px 8px", fontSize: 11, fontWeight: 700, color: "white", background: "#00695c", border: "none", borderRadius: 3, cursor: busy ? "wait" : "pointer" }}>📧 Invia</button>
                          <button type="button" data-testid={`diete-doc-replace-${i}`} disabled={busy} onClick={() => triggerUpload(r)}
                            title="Sostituisci il documento con un nuovo .docx"
                            style={{ padding: "4px 8px", fontSize: 11, fontWeight: 700, color: "#4a148c", background: "white", border: "1px solid #ce93d8", borderRadius: 3, cursor: busy ? "wait" : "pointer" }}>{busy ? "⏳" : "🔁"}</button>
                          <button type="button" data-testid={`diete-doc-delete-${i}`} disabled={busy} onClick={() => deleteDoc(r.cf)}
                            title="Elimina il documento allegato"
                            style={{ padding: "4px 8px", fontSize: 11, fontWeight: 700, color: "#c62828", background: "white", border: "1px solid #ef9a9a", borderRadius: 3, cursor: busy ? "wait" : "pointer" }}>🗑</button>
                        </div>
                        {sentLabel && <div style={{ fontSize: 9, color: "#00695c" }}>✓ inviata il {sentLabel}{doc.last_sent_to ? ` → ${doc.last_sent_to}` : ""}</div>}
                      </div>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dieteModalOpen && (
        <DieteManagerModal items={dieteList}
          onSaved={(newList) => setDieteList(newList)}
          onClose={() => setDieteModalOpen(false)} />
      )}

      <input ref={fileRef} type="file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        style={{ display: "none" }} data-testid="diete-doc-file-input" onChange={onFilePicked} />

      {sendModal && (
        <div onMouseDown={() => !sendModal.sending && setSendModal(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10001, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div data-testid="diete-doc-send-modal" onMouseDown={(e) => e.stopPropagation()}
            style={{ background: "white", borderRadius: 8, width: 480, maxWidth: "94vw", boxShadow: "0 12px 48px rgba(0,0,0,0.35)", overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", background: "#00695c", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>📧 Invia dieta via email</div>
              <button type="button" onClick={() => !sendModal.sending && setSendModal(null)} data-testid="diete-doc-send-close"
                style={{ background: "transparent", border: "none", color: "white", fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ fontSize: 12, color: "#444" }}>Alunno: <b style={{ textTransform: "uppercase" }}>{sendModal.nominativo || "—"}</b></div>
              <label style={{ fontSize: 11, color: "#555", fontWeight: 700 }}>Destinatario</label>
              <input type="email" value={sendModal.email} disabled={sendModal.sending}
                data-testid="diete-doc-send-email"
                onChange={(e) => setSendModal((s) => ({ ...s, email: e.target.value, userEdited: true }))}
                placeholder={sendModal.loading ? "Caricamento email famiglia…" : "indirizzo@email.it"}
                style={{ padding: "8px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13, width: "100%" }} />
              {(sendModal.emails || []).length > 0 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {sendModal.emails.map((em) => (
                    <button key={em} type="button" data-testid={`diete-doc-send-suggest-${em}`}
                      onClick={() => setSendModal((s) => ({ ...s, email: em }))}
                      style={{ padding: "3px 8px", fontSize: 11, background: em === sendModal.email ? "#b2dfdb" : "#eceff1",
                               border: "1px solid #b0bec5", borderRadius: 12, cursor: "pointer", color: "#00695c" }}>{em}</button>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 11, color: "#888" }}>Verrà inviato il PDF della dieta in allegato, con oggetto e testo predefiniti.</div>
            </div>
            <div style={{ padding: "10px 16px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" onClick={() => !sendModal.sending && setSendModal(null)} data-testid="diete-doc-send-cancel"
                style={{ padding: "8px 16px", background: "#eee", color: "#333", border: "none", borderRadius: 4, fontSize: 13, cursor: "pointer" }}>Annulla</button>
              <button type="button" onClick={doSend} disabled={sendModal.sending || sendModal.loading} data-testid="diete-doc-send-confirm"
                style={{ padding: "8px 20px", background: (sendModal.sending || sendModal.loading) ? "#80cbc4" : "#00695c", color: "white", border: "none", borderRadius: 4, fontSize: 13, fontWeight: 700, cursor: (sendModal.sending || sendModal.loading) ? "wait" : "pointer" }}>
                {sendModal.sending ? "Invio…" : sendModal.loading ? "Caricamento…" : "📧 Invia"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
